import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import CheckIcon from "@material-ui/icons/Check";
import FacebookIcon from "@material-ui/icons/Facebook";
import GroupIcon from "@material-ui/icons/Group";
import PublicIcon from "@material-ui/icons/Public";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import TransferTicketModal from "../TransferTicketModal";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { getConnectionPresentationKind } from "../../utils/connections";

const officialConnectionNameCache = new Map();
const officialConnectionRequestCache = new Map();

const extractOfficialConnectionId = ticket => {
	const sessionMatch = String(ticket?.whatsapp?.session || "").match(/^official-(\d+)$/i);
	if (sessionMatch) {
		return Number(sessionMatch[1]);
	}

	const nameMatch = String(ticket?.whatsapp?.name || "")
		.trim()
		.match(/^Official\s+(\d+)$/i);

	if (nameMatch) {
		return Number(nameMatch[1]);
	}

	return null;
};

const isWebchatConnection = ticket => {
	return getConnectionPresentationKind(ticket) === "webchat";
};

const resolveConnectionPresentation = ticket => {
	const kind = getConnectionPresentationKind(ticket);

	if (kind === "official" || extractOfficialConnectionId(ticket)) {
		return {
			kind: "whatsapp",
			icon: WhatsAppIcon,
		};
	}

	if (kind === "webchat" || isWebchatConnection(ticket)) {
		return {
			kind: "webchat",
			icon: PublicIcon,
		};
	}

	if (kind === "facebook") {
		return {
			kind: "facebook",
			icon: FacebookIcon,
		};
	}

	return {
		kind: "whatsapp",
		icon: WhatsAppIcon,
	};
};

const loadOfficialConnectionName = async officialConnectionId => {
	if (!officialConnectionId || Number.isNaN(officialConnectionId)) {
		return "";
	}

	if (officialConnectionNameCache.has(officialConnectionId)) {
		return officialConnectionNameCache.get(officialConnectionId);
	}

	if (officialConnectionRequestCache.has(officialConnectionId)) {
		return officialConnectionRequestCache.get(officialConnectionId);
	}

	const request = api
		.get(`/api/whatsapp-official/${officialConnectionId}`)
		.then(({ data }) => {
			const resolvedName = String(data?.name || "").trim();
			officialConnectionNameCache.set(officialConnectionId, resolvedName);
			officialConnectionRequestCache.delete(officialConnectionId);
			return resolvedName;
		})
		.catch(() => {
			officialConnectionRequestCache.delete(officialConnectionId);
			return "";
		});

	officialConnectionRequestCache.set(officialConnectionId, request);

	return request;
};

const clampColorChannel = value => Math.max(0, Math.min(255, value));

const getDarkerQueueColor = (hexColor, factor = 0.55) => {
	const normalized = String(hexColor || "").trim();
	const shortHex = normalized.match(/^#([0-9a-fA-F]{3})$/);
	if (shortHex) {
		const [r, g, b] = shortHex[1].split("").map(char => parseInt(char + char, 16));
		return `rgb(${clampColorChannel(Math.round(r * factor))}, ${clampColorChannel(
			Math.round(g * factor)
		)}, ${clampColorChannel(Math.round(b * factor))})`;
	}

	const fullHex = normalized.match(/^#([0-9a-fA-F]{6})$/);
	if (fullHex) {
		const r = parseInt(fullHex[1].slice(0, 2), 16);
		const g = parseInt(fullHex[1].slice(2, 4), 16);
		const b = parseInt(fullHex[1].slice(4, 6), 16);
		return `rgb(${clampColorChannel(Math.round(r * factor))}, ${clampColorChannel(
			Math.round(g * factor)
		)}, ${clampColorChannel(Math.round(b * factor))})`;
	}

	return "rgba(0, 0, 0, 0.62)";
};

const getMobileQueueLabel = (queueName, maxLength = 14) => {
	const normalized = String(queueName || "Sem fila").trim();
	if (normalized.length <= maxLength) {
		return normalized;
	}
	return `${normalized.slice(0, Math.max(1, maxLength - 3)).trim()}...`;
};

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
		overflow: "hidden",
		paddingBottom: 2,
		"&::after": {
			content: '""',
			position: "absolute",
			left: 8,
			right: 0,
			bottom: 0,
			height: 1,
			backgroundColor: theme.palette.divider,
		},
	},

	pendingTicket: {
		cursor: "pointer",
	},

	pendingTicketWithAction: {
		paddingRight: 72,
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	contactNameWrapper: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		gap: 8,
	},

	contactNameText: {
		fontWeight: 500,
		minWidth: 0,
		paddingRight: 8,
	},

	headerMeta: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		gap: 8,
		marginLeft: "auto",
		flexShrink: 0,
	},

	contactSecondaryWrapper: {
		display: "flex",
		flexDirection: "column",
		alignItems: "stretch",
		minWidth: 0,
		gap: 6,
	},

	lastMessageTime: {
		justifySelf: "flex-end",
		fontSize: "0.8rem",
		lineHeight: 1,
	},

	closedBadge: {
		alignSelf: "center",
		justifySelf: "flex-end",
		marginRight: 32,
		marginLeft: "auto",
	},

	closedStatusTag: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		border: "none",
		minHeight: 20,
		height: 20,
		lineHeight: "20px",
		padding: "0 8px",
		fontSize: "0.58rem",
	},

	contactLastMessage: {
		minWidth: 0,
		display: "block",
	},

	footerMeta: {
		display: "flex",
		alignItems: "flex-end",
		justifyContent: "space-between",
		gap: 8,
		minWidth: 0,
	},

	footerMetaLeft: {
		display: "flex",
		alignItems: "center",
		gap: 6,
		minWidth: 0,
		flexWrap: "wrap",
	},

	footerMetaRight: {
		display: "flex",
		alignItems: "center",
		gap: 4,
		flexShrink: 0,
		marginLeft: "auto",
	},

	newMessagesCount: {
		alignSelf: "center",
		marginLeft: 2,
		marginRight: 0,
	},

	badgeStyle: {
		color: "white",
		backgroundColor: green[500],
	},

	ticketBody: {
		display: "flex",
		alignItems: "center",
		flex: 1,
		minWidth: 0,
	},

	ticketText: {
		flex: 1,
		minWidth: 0,
	},

	ticketActions: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		marginLeft: 0,
		flex: "none",
		position: "absolute",
		top: 0,
		right: 0,
		width: 56,
		height: "100%",
	},

	acceptButton: {
		minWidth: 0,
		width: "100%",
		height: "100%",
		padding: 0,
		borderRadius: 0,
		boxShadow: "none",
		backgroundColor: "transparent",
		"&:hover": {
			backgroundColor: "transparent",
			boxShadow: "none",
		},
		"&:active": {
			boxShadow: "none",
		},
	},

	ticketQueueColor: {
		flex: "none",
		width: "8px",
		height: "100%",
		position: "absolute",
		top: "0%",
		left: "0%",
		[theme.breakpoints.down("sm")]: {
			width: "14px",
		},
	},

	queueNameMobile: {
		display: "none",
		[theme.breakpoints.down("sm")]: {
			position: "absolute",
			left: 0,
			top: 0,
			bottom: 0,
			width: 14,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			textAlign: "center",
			fontSize: "0.46rem",
			fontWeight: 700,
			lineHeight: 1,
			letterSpacing: 0.4,
			textTransform: "uppercase",
			writingMode: "vertical-rl",
			textOrientation: "mixed",
			transform: "rotate(180deg)",
			maxHeight: "100%",
			overflow: "hidden",
			whiteSpace: "nowrap",
			pointerEvents: "none",
			zIndex: 1,
		},
	},

	metaTag: {
		alignSelf: "center",
		flexShrink: 0,
		minHeight: 22,
		padding: "0 9px",
		borderRadius: 999,
		fontSize: "0.58rem",
		fontWeight: 700,
		lineHeight: "22px",
		textTransform: "uppercase",
		whiteSpace: "nowrap",
	},

	assignedUserTag: {
		display: "inline-flex",
		alignItems: "center",
		padding: 0,
		backgroundColor: "transparent",
	},

	avatarStack: {
		display: "inline-flex",
		alignItems: "center",
		paddingLeft: 4,
	},

	assignedAvatar: {
		width: 24,
		height: 24,
		fontSize: "0.64rem",
		fontWeight: 700,
		border: "2px solid #fff",
		marginLeft: -6,
		backgroundColor: "#d6edf3",
		color: "#4f6b73",
		boxSizing: "border-box",
		"&:first-child": {
			marginLeft: 0,
		},
	},

	assignedAvatarOnline: {
		boxShadow: "0 0 0 2px #2e7d32",
	},

	assignedAvatarOffline: {
		boxShadow: "0 0 0 2px #d32f2f",
	},

	avatarOverflow: {
		width: 24,
		height: 24,
		borderRadius: "50%",
		marginLeft: -6,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: "0.6rem",
		fontWeight: 800,
		color: "#4f6b73",
		backgroundColor: "#eef7fa",
		border: "2px solid #fff",
		boxSizing: "border-box",
	},

	connectionTag: {
		backgroundColor: "transparent",
		color: "#17a65b",
		border: "none",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		verticalAlign: "middle",
		width: 24,
		height: 24,
		minHeight: 24,
		padding: 0,
		borderRadius: "50%",
		transition: theme.transitions.create(["background-color", "box-shadow", "transform"], {
			duration: theme.transitions.duration.shortest,
		}),
		"&:hover": {
			backgroundColor: "rgba(23, 166, 91, 0.12)",
			boxShadow: "0 1px 4px rgba(0,0,0,0.16)",
			transform: "translateY(-1px)",
		},
	},

	connectionTagIcon: {
		display: "block",
		fontSize: "1rem",
		lineHeight: 1,
		flexShrink: 0,
	},

	connectionTagOfficial: {
		color: "#1769aa",
		"&:hover": {
			backgroundColor: "rgba(23, 105, 170, 0.12)",
		},
	},

	connectionTagWebchat: {
		color: "#3150b7",
		"&:hover": {
			backgroundColor: "rgba(49, 80, 183, 0.12)",
		},
	},

	connectionTagFacebook: {
		color: "#1877f2",
		"&:hover": {
			backgroundColor: "rgba(24, 119, 242, 0.12)",
		},
	},

	groupTag: {
		color: "#8a5a00",
		backgroundColor: "transparent",
		border: "none",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		width: 24,
		height: 24,
		minHeight: 24,
		padding: 0,
		borderRadius: "50%",
		transition: theme.transitions.create(["background-color", "box-shadow", "transform"], {
			duration: theme.transitions.duration.shortest,
		}),
		"&:hover": {
			backgroundColor: "rgba(138, 90, 0, 0.14)",
			boxShadow: "0 1px 4px rgba(0,0,0,0.16)",
			transform: "translateY(-1px)",
		},
	},
}));

const TicketListItem = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const initialConnectionLabel = ticket?.connection?.name || ticket?.whatsapp?.name || "";
	const [loading, setLoading] = useState(false);
	const [acceptHover, setAcceptHover] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [connectionLabel, setConnectionLabel] = useState(initialConnectionLabel);
	const { ticketId } = useParams();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const queueColor = ticket.queue?.color || "#7C7C7C";
	const queueLabelMobile = getMobileQueueLabel(ticket.queue?.name || "Sem fila");
	const queueLabelMobileColor = getDarkerQueueColor(queueColor);
	const officialConnectionId = extractOfficialConnectionId(ticket);
	const connectionPresentation = resolveConnectionPresentation(ticket);
	const ConnectionIcon = connectionPresentation.icon;
	const getInitials = name => String(name || "?")
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map(part => part[0])
		.join("")
		.toUpperCase();
	const groupMembers = Array.isArray(ticket?.groupConversation?.members)
		? ticket.groupConversation.members
			.map(member => member.user)
			.filter(Boolean)
		: [];
	const ticketParticipants = Array.isArray(ticket?.participants)
		? ticket.participants
			.map(participant => participant.user)
			.filter(Boolean)
		: [];
	const assignedUsers = ticket.isGroup
		? groupMembers
		: [
			...(ticket.user ? [ticket.user] : []),
			...ticketParticipants.filter(
				participant => Number(participant.id) !== Number(ticket.user?.id)
			),
		];
	const visibleAssignedUsers = assignedUsers.slice(0, 4);
	const hiddenAssignedUsers = Math.max(assignedUsers.length - visibleAssignedUsers.length, 0);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		let active = true;
		setConnectionLabel(ticket?.connection?.name || ticket?.whatsapp?.name || "");

		if (!officialConnectionId) {
			return () => {
				active = false;
			};
		}

		loadOfficialConnectionName(officialConnectionId).then(resolvedName => {
			if (active && resolvedName) {
				setConnectionLabel(resolvedName);
			}
		});

		return () => {
			active = false;
		};
	}, [ticket]);

	const handleAcepptTicket = async id => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
			});
		} catch (err) {
			setLoading(false);
			toastError(err);
			return;
		}
		if (isMounted.current) {
			setLoading(false);
		}
		history.push(`/tickets/${id}`, { switchTab: "open" });
	};

	const handleAcceptButtonClick = (event, id) => {
		event.stopPropagation();
		if (ticket?.queue?.allowManualAttendance === false || !ticket?.queueId) {
			setTransferTicketModalOpen(true);
			return;
		}
		handleAcepptTicket(id);
	};

	const handleCloseTransferTicketModal = () => {
		setTransferTicketModalOpen(false);
	};

	const handleSelectTicket = id => {
		history.push(`/tickets/${id}`);
	};

	return (
		<React.Fragment key={ticket.id}>
			<TransferTicketModal
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
				ticket={ticket}
			/>
			<ListItem
				dense
				button
				onClick={e => handleSelectTicket(ticket.id)}
				selected={ticketId && +ticketId === ticket.id}
				className={clsx(classes.ticket, {
					[classes.pendingTicket]: ticket.status === "pending",
					[classes.pendingTicketWithAction]: ticket.status === "pending",
				})}
			>
				<Tooltip
					arrow
					placement="right"
					title={ticket.queue?.name || "Sem fila"}
				>
					<span>
						<span
							style={{ backgroundColor: queueColor }}
							className={classes.ticketQueueColor}
						></span>
						<span className={classes.queueNameMobile} style={{ color: queueLabelMobileColor }}>
							{queueLabelMobile}
						</span>
					</span>
				</Tooltip>
				<ListItemAvatar>
					<Avatar src={ticket?.contact?.profilePicUrl}>
						{getInitials(ticket?.contact?.name)}
					</Avatar>
				</ListItemAvatar>
				<div className={classes.ticketBody}>
					<ListItemText
						className={classes.ticketText}
						disableTypography
						primary={
							<span className={classes.contactNameWrapper}>
								<Typography
									noWrap
									component="span"
									variant="body2"
									color="textPrimary"
									className={classes.contactNameText}
								>
									{ticket.contact.name}
								</Typography>
								<span className={classes.headerMeta}>
									{ticket.lastMessage && (
										<Typography
											className={classes.lastMessageTime}
											component="span"
											variant="body2"
											color="textSecondary"
										>
											{isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
												<>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
											) : (
												<>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
											)}
										</Typography>
									)}
									{ticket.unreadMessages > 0 && (
										<Badge
											className={classes.newMessagesCount}
											badgeContent={ticket.unreadMessages}
											classes={{
												badge: classes.badgeStyle,
											}}
										/>
									)}
								</span>
							</span>
						}
						secondary={
							<span className={classes.contactSecondaryWrapper}>
								<Typography
									className={classes.contactLastMessage}
									noWrap
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{ticket.lastMessage ? (
										<MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
									) : (
										<br />
									)}
								</Typography>

								<div className={classes.footerMeta}>
									<div className={classes.footerMetaLeft}>
										{assignedUsers.length > 0 && (
											<div className={clsx(classes.metaTag, classes.assignedUserTag)}>
												<div className={classes.avatarStack}>
													{visibleAssignedUsers.map(assignedUser => (
														<Tooltip key={assignedUser.id || assignedUser.name} title={assignedUser.name || ""}>
															<Avatar
																src={assignedUser.profilePicUrl}
																className={clsx(
																	classes.assignedAvatar,
																	typeof assignedUser.isOnline === "boolean" &&
																		(assignedUser.isOnline
																			? classes.assignedAvatarOnline
																			: classes.assignedAvatarOffline)
																)}
															>
																{getInitials(assignedUser.name)}
															</Avatar>
														</Tooltip>
													))}
													{hiddenAssignedUsers > 0 && (
														<span className={classes.avatarOverflow}>
															+{hiddenAssignedUsers}
														</span>
													)}
												</div>
											</div>
										)}
									</div>
									<div className={classes.footerMetaRight}>
										{ticket.status === "closed" && (
											<Tooltip arrow title={i18n.t("ticketsList.closedTag")}>
												<span className={clsx(classes.metaTag, classes.closedStatusTag)}>
													{i18n.t("ticketsList.closedTag")}
												</span>
											</Tooltip>
										)}
										{(ticket.connectionId || ticket.whatsappId) && connectionLabel && (
											<Tooltip arrow title={connectionLabel || i18n.t("ticketsList.connectionTitle")}>
												<span
													className={clsx(
														classes.metaTag,
														classes.connectionTag,
														connectionPresentation.kind === "official" &&
															classes.connectionTagOfficial,
														connectionPresentation.kind === "webchat" &&
															classes.connectionTagWebchat,
														connectionPresentation.kind === "facebook" &&
															classes.connectionTagFacebook
													)}
												>
													<ConnectionIcon className={classes.connectionTagIcon} />
												</span>
											</Tooltip>
										)}
										{ticket.isGroup && (
											<Tooltip arrow title={i18n.t("ticketsList.groupTag")}>
												<span className={clsx(classes.metaTag, classes.groupTag)}>
													<GroupIcon className={classes.connectionTagIcon} />
												</span>
											</Tooltip>
										)}
									</div>
								</div>
							</span>
						}
					/>
					{ticket.status === "pending" && (
					<div
						className={classes.ticketActions}
						style={{
						  backgroundColor: acceptHover ? (ticket.queue?.color || "#7C7C7C") : "transparent",
						  transition: "background-color 0.2s ease",
						}}
						onMouseEnter={() => setAcceptHover(true)}
						onMouseLeave={() => setAcceptHover(false)}
					>
							<ButtonWithSpinner
							color="inherit"
							variant="text"
								className={classes.acceptButton}
								style={{ color: acceptHover ? "#fff" : "rgba(0,0,0,0.54)" }}
								size="small"
								loading={loading}
								onClick={e => handleAcceptButtonClick(e, ticket.id)}
								aria-label={i18n.t("ticketsList.buttons.accept")}
								title={i18n.t("ticketsList.buttons.accept")}
							>
								<CheckIcon fontSize="small" />
							</ButtonWithSpinner>
						</div>
					)}
				</div>
			</ListItem>
		</React.Fragment>
	);
};

export default TicketListItem;
